import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";

export function LandingPage() {
  const revealRefs = useRef<HTMLElement[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("opacity-100", "translate-y-0");
            entry.target.classList.remove("opacity-0", "translate-y-6");
          }
        });
      },
      { threshold: 0.1 },
    );
    revealRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const reveal = (el: HTMLElement | null) => {
    if (el && !revealRefs.current.includes(el)) revealRefs.current.push(el);
  };

  return (
    <>
      {/* Nav */}
      <nav
        className="fixed top-0 left-0 right-0 z-50 h-14 flex items-center justify-between px-4 md:px-6 border-b border-white/6"
        style={{ background: "rgba(14,26,42,0.92)", backdropFilter: "blur(12px)" }}
      >
        <span className="font-display text-xl spectral-gradient-text">Espectro</span>
        <div className="flex items-center gap-5 md:gap-8">
          <a href="#how" className="hidden md:inline font-heading text-sm text-ash hover:text-ghost-white transition-colors">
            How it works
          </a>
          <a href="#why" className="hidden md:inline font-heading text-sm text-ash hover:text-ghost-white transition-colors">
            Why Zcash
          </a>
          <Link
            to="/app"
            className="px-4 py-1.5 rounded-lg text-white font-heading text-sm font-semibold transition-all hover:brightness-110"
            style={{ background: "#3b83da" }}
          >
            Launch App
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="min-h-screen flex flex-col items-center justify-center px-6 pt-20 pb-16 text-center relative overflow-hidden">
        {/* Subtle background — single, small, no animation */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 60% 40% at 50% 60%, rgba(124,58,237,0.07) 0%, transparent 70%)",
          }}
        />

        <div className="relative z-10 max-w-2xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/10 text-xs font-heading text-ash mb-10" style={{ background: "#122134" }}>
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0" />
            Live on Base
          </div>

          <h1 className="font-display text-[clamp(3rem,9vw,6.5rem)] leading-[0.92] font-normal text-ghost-white mb-6 animate-fade-up">
            Send funds<br />
            <span className="spectral-gradient-text italic">Leave no trace</span>
          </h1>

          <p className="font-heading text-base md:text-lg text-mist font-light mb-10 max-w-lg mx-auto leading-relaxed animate-fade-up [animation-delay:0.1s]">
            Espectro routes your transfer through Zcash's Orchard shielded pool — breaking the on-chain link between source and destination.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 animate-fade-up [animation-delay:0.2s]">
            <Link
              to="/app"
              className="px-7 py-3 rounded-lg text-white font-heading text-sm font-semibold hover:brightness-110 transition-all"
              style={{ background: "#3b83da" }}
            >
              Start a transfer
            </Link>
            <a
              href="#how"
              className="px-7 py-3 rounded-lg text-mist font-heading text-sm border border-white/10 hover:border-white/20 hover:text-ghost-white transition-all"
            >
              See how it works
            </a>
          </div>

          <p className="mt-8 text-xs text-ash/60 font-body animate-fade-up [animation-delay:0.3s]">
            ~20 min · 2–4% cost · no account required
          </p>
        </div>
      </section>

      {/* Stat bar */}
      <div className="py-10 px-6">
        <div className="max-w-[900px] mx-auto flex flex-col md:flex-row items-stretch gap-3">
          {[
            { v: "Zcash", l: "Privacy layer", bg: "#7c3aed", offset: "md:translate-y-2" },
            { v: "Non-custodial", l: "Funds stay yours", bg: "#6366f1", offset: "md:-translate-y-3" },
            { v: "Near Intents", l: "Settlement layer", bg: "#06b6d4", offset: "md:translate-y-0" },
          ].map(({ v, l, bg, offset }) => (
            <div
              key={l}
              className={`flex-1 rounded-2xl px-8 py-6 text-center transition-transform duration-300 hover:-translate-y-1 ${offset}`}
              style={{
                background: bg,
                boxShadow: `0 12px 40px ${bg}60`,
              }}
            >
              <div className="font-heading text-base font-semibold text-white mb-1">{v}</div>
              <div className="text-xs font-body text-white/55">{l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* How it works */}
      <section id="how" className="max-w-[1000px] mx-auto px-6 py-24">
        <div ref={reveal} className="opacity-0 translate-y-6 transition-all duration-700 mb-16">
          <p className="font-heading text-xs font-semibold uppercase tracking-[0.15em] text-spectral-violet mb-3">
            How it works
          </p>
          <h2 className="font-heading text-[clamp(1.8rem,3.5vw,2.5rem)] font-semibold text-ghost-white tracking-tight">
            Three steps. One signature.
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {STEPS.map((step, i) => (
            <div
              key={step.title}
              ref={reveal}
              className="relative p-6 rounded-2xl overflow-hidden flex flex-col opacity-0 translate-y-6 transition-all duration-700 hover:-translate-y-1.5"
              style={{ background: step.bg, transitionDelay: `${i * 0.12}s` }}
            >
              {/* Large decorative step number */}
              <span
                className="absolute top-3 right-4 font-display text-[4.5rem] leading-none font-bold select-none pointer-events-none opacity-10"
                style={{ color: "#0e1a2a" }}
              >
                {step.num}
              </span>

              <div className="relative z-10">
                <div className="font-heading text-2xl font-semibold mb-8" style={{ color: "#0e1a2a" }}>
                  {step.title}
                </div>
                <div className="text-sm leading-relaxed" style={{ color: "#1e3a5f" }}>{step.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* The problem / why */}
      <section
        ref={reveal}
        id="why"
        className="max-w-[1000px] mx-auto px-6 py-8 pb-24 opacity-0 translate-y-6 transition-all duration-700"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <div>
            <p className="font-heading text-xs font-semibold uppercase tracking-[0.15em] text-spectral-violet mb-3">
              The problem
            </p>
            <h2 className="font-heading text-[clamp(1.8rem,3.5vw,2.5rem)] font-semibold text-ghost-white mb-5 tracking-tight">
              Every on-chain move is public.
            </h2>
            <p className="text-sm text-ash leading-relaxed mb-4">
              Blockchains are transparent by design. Any address, balance, or transfer is visible to anyone — forever. Employers, advertisers, and anyone with a blockchain explorer can reconstruct your full financial history from a single address.
            </p>
            <p className="text-sm text-ash leading-relaxed">
              Espectro breaks that link. Deposit from one address, withdraw to another. No chain connects them.
            </p>
          </div>

          {/* Mini diagram */}
          <div className="flex flex-col gap-3">
            {[
              { label: "0x1a2b…cd3e", tag: "Source wallet", dim: false },
              { label: "Zcash Orchard pool", tag: "Shielded · untraceable", dim: true, accent: true },
              { label: "0x9f8e…ba1c", tag: "Destination wallet", dim: false },
            ].map((row, i) => (
              <div key={i} className="flex items-center gap-3">
                {i === 1 && (
                  <div className="flex flex-col items-center self-stretch py-1 mr-0">
                    <div className="w-px flex-1 bg-white/8" />
                    <div className="w-px flex-1 bg-white/8" />
                  </div>
                )}
                <div
                  className={`flex-1 flex items-center justify-between px-4 py-3 rounded-xl border ${row.accent ? "border-spectral-violet/25" : "border-white/6"}`}
                  style={{ background: row.accent ? "rgba(124,58,237,0.06)" : "#122134" }}
                >
                  <span className={`font-mono text-xs ${row.accent ? "spectral-gradient-text font-medium" : "text-ghost-white"}`}>
                    {row.label}
                  </span>
                  <span className="text-[0.6rem] text-ash/50 font-heading uppercase tracking-wide">
                    {row.tag}
                  </span>
                </div>
              </div>
            ))}
            <div className="flex items-center gap-2 mt-2 px-1">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="text-emerald-400 shrink-0">
                <path d="M2.5 7.5L6 11L11.5 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span className="text-xs text-ash/60 font-body">No on-chain link between source and destination</span>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-[1000px] mx-auto px-6 py-8 pb-24">
        <div ref={reveal} className="opacity-0 translate-y-6 transition-all duration-700 mb-12">
          <p className="font-heading text-xs font-semibold uppercase tracking-[0.15em] text-spectral-violet mb-3">
            Under the hood
          </p>
          <h2 className="font-heading text-[clamp(1.8rem,3.5vw,2.5rem)] font-semibold text-ghost-white tracking-tight">
            Why this works.
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {FEATURES.map((f, i) => (
            <div
              key={f.title}
              ref={reveal}
              className="relative rounded-2xl overflow-hidden flex flex-col p-6 opacity-0 translate-y-6 transition-all duration-700 hover:-translate-y-1.5"
              style={{ background: f.bg, transitionDelay: `${i * 0.1}s` }}
            >
              {/* Abstract bg decor */}
              <div className="absolute top-0 right-0 w-28 h-28 opacity-10 pointer-events-none">
                {f.bgDecor}
              </div>

              <div className="relative z-10 flex flex-col gap-3">
                <div className="font-heading text-base font-semibold" style={{ color: "#0e1a2a" }}>
                  {f.title}
                </div>
                <div className="text-sm leading-relaxed" style={{ color: "#1e3a5f" }}>{f.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section
        ref={reveal}
        className="px-6 py-24 text-center opacity-0 translate-y-6 transition-all duration-700 border-t border-white/6"
      >
        <h2 className="font-display text-[clamp(2rem,5vw,3.5rem)] font-normal text-ghost-white mb-4 tracking-tight">
          Your transfer, your business.
        </h2>
        <p className="text-sm text-ash mb-8 max-w-md mx-auto leading-relaxed">
          No sign-up. No custody. No record. Connect your wallet and go.
        </p>
        <Link
          to="/app"
          className="inline-flex items-center gap-2 px-7 py-3 rounded-lg text-white font-heading text-sm font-semibold hover:brightness-110 transition-all"
          style={{ background: "#3b83da" }}
        >
          Start a transfer
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M2 7H12M8 3L12 7L8 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </Link>
      </section>

      {/* Footer */}
      <footer className="max-w-[1000px] mx-auto px-6 py-8 border-t border-white/6 flex items-center justify-between">
        <span className="font-display text-sm text-ash/50">Espectro</span>
        <div className="flex items-center gap-6">
          <a
            href="mailto:espectrosupport@proton.me"
            className="text-xs text-ash/40 hover:text-ash transition-colors font-body"
          >
            Support
          </a>
        </div>
      </footer>
    </>
  );
}

const STEPS = [
  {
    title: "Configure",
    desc: "Choose your source token, amount, and destination address. One screen.",
    num: "01",
    bg: "#bfdbfe",
  },
  {
    title: "Sign once",
    desc: "Approve one transaction in your wallet. Everything else runs automatically in the background.",
    num: "02",
    bg: "#93c5fd",
  },
  {
    title: "Receive",
    desc: "Funds arrive at the destination with no on-chain connection to their origin. Nothing is stored.",
    num: "03",
    bg: "#60a5fa",
  },
];

const FEATURES = [
  {
    title: "Zcash Orchard pool",
    desc: "Transfers pass through the same shielded pool used by the entire Zcash network — not a custom smart contract with a small anonymity set.",
    bg: "#bfdbfe",
    bgDecor: (
      <svg width="112" height="112" viewBox="0 0 112 112" fill="none">
        <circle cx="56" cy="56" r="50" stroke="#0e1a2a" strokeWidth="0.8" />
        <circle cx="56" cy="56" r="34" stroke="#0e1a2a" strokeWidth="0.8" />
        <circle cx="56" cy="56" r="18" stroke="#0e1a2a" strokeWidth="0.8" />
        <line x1="56" y1="6" x2="56" y2="106" stroke="#0e1a2a" strokeWidth="0.8" />
        <line x1="6" y1="56" x2="106" y2="56" stroke="#0e1a2a" strokeWidth="0.8" />
      </svg>
    ),
  },
  {
    title: "Non-custodial",
    desc: "Espectro never holds your funds. A temporary Zcash wallet is created for your transfer and used exactly once.",
    bg: "#93c5fd",
    bgDecor: (
      <svg width="112" height="112" viewBox="0 0 112 112" fill="none">
        <rect x="14" y="14" width="84" height="84" rx="8" stroke="#0e1a2a" strokeWidth="0.8" />
        <rect x="30" y="30" width="52" height="52" rx="5" stroke="#0e1a2a" strokeWidth="0.8" />
        <rect x="46" y="46" width="20" height="20" rx="3" stroke="#0e1a2a" strokeWidth="0.8" />
      </svg>
    ),
  },
  {
    title: "No stored data",
    desc: "No accounts, no database, no server logs of your activity. When the transfer completes, there is nothing to subpoena.",
    bg: "#60a5fa",
    bgDecor: (
      <svg width="112" height="112" viewBox="0 0 112 112" fill="none">
        <line x1="14" y1="28" x2="98" y2="28" stroke="#0e1a2a" strokeWidth="0.8" />
        <line x1="14" y1="44" x2="98" y2="44" stroke="#0e1a2a" strokeWidth="0.8" />
        <line x1="14" y1="60" x2="98" y2="60" stroke="#0e1a2a" strokeWidth="0.8" />
        <line x1="14" y1="76" x2="98" y2="76" stroke="#0e1a2a" strokeWidth="0.8" />
        <line x1="14" y1="14" x2="98" y2="98" stroke="#0e1a2a" strokeWidth="1.2" />
      </svg>
    ),
  },
  {
    title: "Any EVM destination",
    desc: "Send ETH from Base today. Ethereum, Arbitrum, Polygon and more chains are on the roadmap.",
    bg: "#3b82f6",
    bgDecor: (
      <svg width="112" height="112" viewBox="0 0 112 112" fill="none">
        <circle cx="20" cy="56" r="8" stroke="#0e1a2a" strokeWidth="0.8" />
        <circle cx="56" cy="20" r="8" stroke="#0e1a2a" strokeWidth="0.8" />
        <circle cx="92" cy="56" r="8" stroke="#0e1a2a" strokeWidth="0.8" />
        <circle cx="56" cy="92" r="8" stroke="#0e1a2a" strokeWidth="0.8" />
        <circle cx="56" cy="56" r="8" stroke="#0e1a2a" strokeWidth="0.8" />
        <line x1="28" y1="56" x2="48" y2="56" stroke="#0e1a2a" strokeWidth="0.8" />
        <line x1="64" y1="56" x2="84" y2="56" stroke="#0e1a2a" strokeWidth="0.8" />
        <line x1="56" y1="28" x2="56" y2="48" stroke="#0e1a2a" strokeWidth="0.8" />
        <line x1="56" y1="64" x2="56" y2="84" stroke="#0e1a2a" strokeWidth="0.8" />
      </svg>
    ),
  },
];
